import { useEffect, useRef, useState } from "react";
import { Check, Copy, ImagePlus, Link2, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { fmtDate } from "../../lib/format";
import { mediaUrl } from "../../components/storefront/HomeSections";

export default function MediaPage() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => {
    const params = q ? `?q=${encodeURIComponent(q)}` : "";
    api.get(`/admin/homepage/media${params}`).then(({ data }) => setItems(data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(load, [q]);

  const uploadFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    let ok = 0;
    for (const f of list) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("alt_text", f.name.replace(/\.[^.]+$/, ""));
      try { await api.post("/admin/homepage/media", fd); ok++; }
      catch (e) { toast.error(`${f.name}: ${errMsg(e)}`); }
    }
    setUploading(false);
    if (ok) toast.success(`${ok} görsel yüklendi`);
    load();
  };

  const remove = async (m) => {
    if (m.used_in?.length) {
      toast.error(`Bu görsel ${m.used_in.length} bölümde kullanılıyor. Önce bölümlerden kaldırın.`);
      return;
    }
    if (!window.confirm(`"${m.filename}" silinsin mi?`)) return;
    try {
      await api.delete(`/admin/homepage/media/${m.media_id}`);
      toast.success("Görsel silindi");
      if (selected?.media_id === m.media_id) setSelected(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const copyUrl = (url) => {
    navigator.clipboard.writeText(mediaUrl(url));
    toast.success("URL kopyalandı");
  };

  return (
    <div className="space-y-5" data-testid="media-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display font-extrabold text-2xl text-white">Medya Kütüphanesi</h1>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Dosya adında ara..."
            className="h-10 pl-9 pr-4 bg-[#181B20] border border-[#272B33] rounded-xl text-sm text-white w-64 focus:outline-none focus:border-blue-500"
            data-testid="media-search" />
        </div>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); uploadFiles(e.dataTransfer.files); }}
        onClick={() => fileRef.current?.click()}
        className={`flex flex-col items-center justify-center gap-2 h-32 border-2 border-dashed rounded-2xl cursor-pointer transition-colors ${dragging ? "border-blue-500 bg-blue-600/10 text-blue-400" : "border-[#272B33] text-neutral-400 hover:text-white hover:border-blue-500"}`}
        data-testid="media-dropzone">
        <ImagePlus size={22} />
        <p className="text-sm font-medium">{uploading ? "Yükleniyor..." : "Görselleri buraya sürükleyin veya tıklayıp seçin"}</p>
        <p className="text-[11px] text-neutral-600">JPG, PNG, WEBP, SVG · maks 10MB · çoklu seçim destekli · WebP otomatik optimize (orijinal korunur)</p>
        <input ref={fileRef} type="file" multiple accept="image/jpeg,image/png,image/webp,image/svg+xml" className="hidden"
          onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="media-file-input" />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {items?.map((m) => (
          <button key={m.media_id} onClick={() => setSelected(m)}
            className={`bg-[#181B20] border rounded-xl overflow-hidden text-left transition-colors ${selected?.media_id === m.media_id ? "border-blue-500" : "border-[#272B33] hover:border-neutral-600"}`}
            data-testid={`media-item-${m.media_id}`}>
            <img src={mediaUrl(m.optimized_url || m.url)} alt={m.alt_text} loading="lazy" className="w-full h-28 object-cover bg-[#0F1115]" />
            <div className="p-2">
              <p className="text-[10px] text-neutral-400 truncate">{m.filename}</p>
              <p className="text-[9px] text-neutral-600">
                {m.width ? `${m.width}×${m.height} · ` : ""}{(m.size / 1024).toFixed(0)} KB
                {m.used_in?.length > 0 && <span className="text-amber-500"> · {m.used_in.length} yerde</span>}
              </p>
            </div>
          </button>
        ))}
        {items?.length === 0 && <p className="text-sm text-neutral-500 col-span-full py-10 text-center">Görsel bulunamadı</p>}
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="media-detail-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSelected(null)} />
          <div className="relative bg-[#13161B] border border-[#272B33] rounded-2xl p-6 w-full max-w-2xl">
            <div className="flex justify-between items-start mb-4">
              <h3 className="font-display font-bold text-white truncate pr-4">{selected.filename}</h3>
              <button onClick={() => setSelected(null)} className="text-neutral-400 hover:text-white shrink-0"><X size={18} /></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <img src={mediaUrl(selected.url)} alt={selected.alt_text} className="w-full rounded-xl bg-[#0F1115] object-contain max-h-72" />
              <div className="space-y-2 text-xs">
                <p className="text-neutral-400"><span className="text-neutral-600">Tür:</span> {selected.content_type}</p>
                <p className="text-neutral-400"><span className="text-neutral-600">Boyut:</span> {(selected.size / 1024).toFixed(1)} KB</p>
                {selected.width && <p className="text-neutral-400"><span className="text-neutral-600">Ölçü:</span> {selected.width}×{selected.height}px</p>}
                <p className="text-neutral-400"><span className="text-neutral-600">Alt text:</span> {selected.alt_text || "—"}</p>
                <p className="text-neutral-400"><span className="text-neutral-600">Yükleme:</span> {fmtDate(selected.created_at)}</p>
                {selected.optimized_url && <p className="text-emerald-400 flex items-center gap-1"><Check size={12} /> WebP optimize sürüm mevcut</p>}
                <div>
                  <p className="text-neutral-600 mb-1">Kullanıldığı bölümler:</p>
                  {selected.used_in?.length ? selected.used_in.map((u) => (
                    <p key={u.section_id} className="text-amber-400">{u.title} <span className="text-neutral-600">({u.type})</span></p>
                  )) : <p className="text-neutral-500">Henüz kullanılmıyor</p>}
                </div>
                <div className="flex gap-2 pt-3">
                  <button onClick={() => copyUrl(selected.optimized_url || selected.url)} className="h-9 px-3 rounded-xl bg-blue-600/15 text-blue-400 text-xs font-semibold flex items-center gap-1.5 hover:bg-blue-600/25" data-testid="media-copy-url">
                    <Copy size={13} /> URL Kopyala
                  </button>
                  <button onClick={() => copyUrl(selected.url)} className="h-9 px-3 rounded-xl bg-[#181B20] border border-[#272B33] text-neutral-300 text-xs font-semibold flex items-center gap-1.5 hover:text-white" data-testid="media-copy-original">
                    <Link2 size={13} /> Orijinal URL
                  </button>
                  <button onClick={() => remove(selected)} className="h-9 px-3 rounded-xl bg-rose-600/15 text-rose-400 text-xs font-semibold flex items-center gap-1.5 hover:bg-rose-600/25" data-testid="media-delete">
                    <Trash2 size={13} /> Sil
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
